import { useState } from "react"
import styled from "styled-components"

import {
  CircularProgress,
  colors,
  spacing,
  Text,
} from "@minuthq/meatball-ui-react"

import { Pager } from "src/components/Pager/Pager"
import { useUrlPager } from "src/components/Pager/useUrlPager"
import { ParadiseTable } from "src/components/Paradise/ParadiseTable"
import {
  EmailDialog,
  POSTMARK_RETENTION_DAYS,
} from "src/components/Paradise/ParadiseUsers/ParadiseUserDetails/ParadiseUserEmails/ParadiseUserEmailDialog"
import {
  useFetchParadiseUserEmail,
  useFetchParadiseUserEmails,
} from "src/data/paradise/paradiseUsers/queries/paradiseUserQueries"
import { IParadiseUserEmail } from "src/data/paradise/paradiseUsers/types/paradiseUserQueryTypes"
import { TextButton } from "src/ui/Button/TextButton"
import {
  TableColumn,
  useTableColumns,
} from "src/ui/GridTable/useTableColumns/useTableColumns"
import IIcon from "src/ui/icons/i-icon.svg"
import { MBadge } from "src/ui/MBadge/MBadge"
import { MBanner } from "src/ui/MBanner/MBanner"
import { formatDate } from "src/utils/l10n"

export function ParadiseUserEmails({ userId }: { userId: string }) {
  const { limit, offset, setOffset } = useUrlPager({ initialLimit: 10 })
  const [selectedEmailId, setSelectedEmailId] = useState<string | null>(null)

  const fetchParadiseUserEmails = useFetchParadiseUserEmails({
    userId,
    filter: {
      limit,
      offset,
    },
  })

  const { data: emails, isLoading } = fetchParadiseUserEmails

  const headers: TableColumn<IParadiseUserEmail>[] = [
    {
      value: "sent_at",
      label: "Sent at",
      columnWidth: "auto",
      renderCell: (email) => (
        <Text variant="bodyS">{formatDate({ date: email.sent_at })}</Text>
      ),
    },
    {
      value: "status",
      label: "Status",
      columnWidth: "auto",
      renderCell: (email) => (
        <Text variant="bodyS">
          <EmailBadge userId={userId} emailId={email.email_id} />
        </Text>
      ),
    },
    {
      value: "subject",
      label: "Subject",
      columnWidth: "1fr",
      renderCell: (email) => <Text variant="bodyS">{email.subject}</Text>,
    },
    {
      value: "actions",
      label: "",
      columnWidth: "auto",
      renderCell: (email) => (
        <OpenEmailButton onClick={() => setSelectedEmailId(email.email_id)}>
          <ContentButton>
            <IIcon color="black" width={12} />
          </ContentButton>
        </OpenEmailButton>
      ),
    },
  ]

  const { headerElements, rows, templateColumns } = useTableColumns({
    columns: headers,
    data: emails?.emails ?? [],
  })

  return (
    <div>
      <EmailDialog
        userId={userId}
        open={!!selectedEmailId}
        onClose={() => setSelectedEmailId(null)}
        emailId={selectedEmailId}
      />
      <ParadiseTable
        header={headerElements}
        templateColumns={templateColumns}
        isLoading={isLoading}
        rows={rows ?? []}
      />

      <Pager
        limit={limit}
        offset={offset}
        totalCount={fetchParadiseUserEmails.data?.paging.total_count}
        setOffset={setOffset}
      />

      <MBanner type="info" size="small">
        Emails are stored for {POSTMARK_RETENTION_DAYS} days
      </MBanner>
    </div>
  )
}

function EmailBadge({ userId, emailId }: { userId: string; emailId: string }) {
  // This query is not enabled until the user clicks the status badge
  const [isEnabled, setIsEnabled] = useState(false)
  const fetchSelectedEmail = useFetchParadiseUserEmail({
    userId,
    emailId: emailId,
    options: {
      enabled: isEnabled,
      onSuccess: () => setIsEnabled(false),
    },
  })

  const emailDetails = fetchSelectedEmail.data

  if (fetchSelectedEmail.isFetching) {
    return (
      <MBadge color="info">
        <CircularProgress size={10} />
      </MBadge>
    )
  }

  if (emailDetails?.opened === true) {
    return <MBadge color="good">Opened</MBadge>
  }

  if (emailDetails?.opened === false) {
    return <MBadge color="warning">Not opened</MBadge>
  }

  return (
    <div onClick={() => setIsEnabled(true)} style={{ cursor: "pointer" }}>
      <MBadge color="info">Click to load</MBadge>
    </div>
  )
}

const OpenEmailButton = styled.div`
  cursor: pointer;
`

const ContentButton = styled(TextButton)`
  background-color: ${colors.divider};
  padding: ${spacing.XS};
  border-radius: 5px;
  transition: all 0.2s;

  &:hover {
    background-color: ${colors.dividerDark};
  }
`
